/**
 * Cleo ERP SaaS — Liste des abonnements billing admin
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Tag, Input, Select, Card, Typography, Space,
  Row, Col, Statistic, Alert, Badge,
} from 'antd';
import {
  SearchOutlined, DollarOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, StopOutlined,
} from '@ant-design/icons';
import api from '../../../api/axios';

const { Title, Text } = Typography;

const STATUS_COLORS = {
  ACTIVE: '#10B981',
  TRIAL: '#3B82F6',
  PAST_DUE: '#F59E0B',
  SUSPENDED: '#64748B',
  CANCELLATION_PENDING: '#F97316',
  CANCELLED: '#EF4444',
};

const STATUS_LABELS = {
  ACTIVE: 'Actif',
  TRIAL: "Période d'essai",
  PAST_DUE: 'Paiement en retard',
  SUSPENDED: 'Suspendu',
  CANCELLATION_PENDING: 'Annulation programmée',
  CANCELLED: 'Annulé',
};

const CURRENCY_LABELS = { EUR: '€', XOF: 'XOF', MAD: 'MAD' };

function BillingSubscriptionListPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const [subsRes, statsRes] = await Promise.all([
        api.get('/api/admin/billing/subscriptions/', { params }),
        api.get('/api/admin/billing/stats/'),
      ]);
      setSubscriptions(subsRes.data);
      setStats(statsRes.data);
    } catch {
      setError('Impossible de charger les abonnements.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const columns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_slug',
      key: 'tenant_slug',
      render: (slug, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#0F172A' }}>{slug}</Text>
          <Text style={{ color: '#64748B', fontSize: 12 }}>{record.company_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (s, record) => (
        <Space>
          <Tag color={STATUS_COLORS[s] || '#CBD5E1'} style={{ borderRadius: 6 }}>
            {STATUS_LABELS[s] || s}
          </Tag>
          {record.has_pending_cancellation && (
            <Badge color="#F97316" text={<Text style={{ fontSize: 11, color: '#F97316' }}>Annulation</Text>} />
          )}
        </Space>
      ),
    },
    {
      title: 'Montant mensuel',
      key: 'monthly_cost',
      render: (_, r) => (
        <Text strong style={{ color: '#10B981' }}>
          {parseFloat(r.monthly_cost).toLocaleString('fr-FR')} {CURRENCY_LABELS[r.currency] || r.currency}
        </Text>
      ),
    },
    {
      title: 'Prochaine facturation',
      dataIndex: 'next_billing_date',
      key: 'next_billing_date',
      render: (date) => date
        ? new Date(date).toLocaleDateString('fr-FR')
        : <Text style={{ color: '#94A3B8' }}>—</Text>,
    },
    {
      title: 'Échecs paiement',
      dataIndex: 'payment_failure_count',
      key: 'payment_failure_count',
      render: (count) => count > 0
        ? <Tag color="#EF4444" style={{ borderRadius: 6 }}>{count}</Tag>
        : <Text style={{ color: '#94A3B8' }}>0</Text>,
    },
  ];

  if (error) return <Alert message={error} type="error" showIcon />;

  return (
    <div>
      <Title level={4} style={{ color: '#0F172A', marginBottom: 16 }}>Abonnements</Title>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {[
            { label: 'Actifs', value: stats.subscriptions_active, color: '#10B981', icon: <CheckCircleOutlined /> },
            { label: 'En retard', value: stats.subscriptions_past_due, color: '#F59E0B', icon: <ClockCircleOutlined /> },
            { label: 'Annulations', value: stats.pending_cancellation_requests, color: '#F97316', icon: <ExclamationCircleOutlined /> },
            { label: 'Suspendus', value: stats.subscriptions_suspended, color: '#64748B', icon: <StopOutlined /> },
          ].map((item) => (
            <Col xs={12} sm={6} key={item.label}>
              <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <Statistic
                  title={<Text style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase' }}>{item.label}</Text>}
                  value={item.value}
                  prefix={React.cloneElement(item.icon, { style: { color: item.color } })}
                  valueStyle={{ color: item.color, fontSize: 22, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {stats && (
        <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 16 }}>
          <Space size={32}>
            <Space>
              <DollarOutlined style={{ color: '#10B981' }} />
              <Text strong>MRR EUR :</Text>
              <Text style={{ color: '#10B981' }}>{stats.mrr_eur.toLocaleString('fr-FR')} €</Text>
            </Space>
            <Space>
              <DollarOutlined style={{ color: '#10B981' }} />
              <Text strong>MRR XOF :</Text>
              <Text style={{ color: '#10B981' }}>{stats.mrr_xof.toLocaleString('fr-FR')} XOF</Text>
            </Space>
            {stats.failed_webhook_events_24h > 0 && (
              <Tag color="#EF4444">
                {stats.failed_webhook_events_24h} webhook(s) en échec (24h)
              </Tag>
            )}
          </Space>
        </Card>
      )}

      <Card size="small" style={{ borderRadius: 12, border: '1px solid #E5E7EB', marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Rechercher tenant..."
            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
            style={{ width: 240 }}
            allowClear
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
          <Select
            placeholder="Statut"
            style={{ width: 180 }}
            allowClear
            onChange={(val) => setFilters(f => ({ ...f, status: val || '' }))}
            options={Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v }))}
          />
        </Space>
      </Card>

      <Card style={{ borderRadius: 12, border: '1px solid #E5E7EB' }}>
        <Table
          columns={columns}
          dataSource={subscriptions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="middle"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/billing/subscriptions/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
}

export default BillingSubscriptionListPage;
